import React from "react";
import { Link } from "react-router-dom";
import {
  Clock,
  MapPin,
  Camera,
  CalendarDays,
  CheckCircle,
  GitPullRequest,
  ArrowRight,
  ShieldCheck,
  BarChart3
} from "lucide-react";

export default function AttendanceAndLeave() {
  const poppinsStyle = { fontFamily: "'Poppins', sans-serif" };
  const robotoStyle = { fontFamily: "'Roboto', sans-serif" };

  return (
    <div className="min-h-screen bg-white" style={robotoStyle}>
      {/* Hero Section - Background set to white */}
      <div className="bg-white py-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium mb-6" style={poppinsStyle}>
            <Clock size={16} />
            Real-Time Workforce Tracking
          </div>
          
          {/* Title reduced to text-3xl (Medium) and color unified to gray-900 */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 leading-tight" style={poppinsStyle}>
            Smart Attendance & <br />
            Automated Leave Management
          </h1>
          
          <p className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Eliminate buddy punching and manual entry. Our GPS-enabled, selfie-verified 
            system ensures 100% accuracy in attendance while automating complex leave accruals.
          </p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-20">
        {/* Core Features Grid */}
        <div className="grid md:grid-cols-2 gap-12 items-start mb-20">
          <div className="space-y-8">
            <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2" style={poppinsStyle}>
              <CheckCircle className="text-slate-900" size={24} />
              Precision Tracking
            </h2>
            
            <div className="grid gap-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-slate-600 border border-slate-100">
                  <MapPin size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm" style={poppinsStyle}>GPS Geofencing</h4>
                  <p className="text-sm text-gray-600">Restrict punches to specific office coordinates or client locations.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-slate-600 border border-slate-100">
                  <Camera size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm" style={poppinsStyle}>Selfie Verification</h4>
                  <p className="text-sm text-gray-600">AI-powered face recognition to prevent proxy attendance.</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center text-slate-600 border border-slate-100">
                  <GitPullRequest size={20} />
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 text-sm" style={poppinsStyle}>Multi-Level Approvals</h4>
                  <p className="text-sm text-gray-600">Automated workflows that route requests to managers instantly.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
            <h2 className="text-xl font-semibold text-gray-900 mb-6" style={poppinsStyle}>Operational Insights</h2>
            <div className="space-y-4">
              <div className="bg-slate-50 p-4 rounded-xl flex items-center justify-between border border-slate-100">
                <div className="flex items-center gap-3">
                  <CalendarDays className="text-slate-600" size={18} />
                  <span className="text-sm font-medium">Shift Rostering</span>
                </div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Auto</span>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl flex items-center justify-between border border-slate-100">
                <div className="flex items-center gap-3">
                  <BarChart3 className="text-slate-600" size={18} />
                  <span className="text-sm font-medium">Leave Accrual</span>
                </div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Config</span>
              </div>
              <div className="bg-slate-50 p-4 rounded-xl flex items-center justify-between border border-slate-100">
                <div className="flex items-center gap-3">
                  <ShieldCheck className="text-slate-600" size={18} />
                  <span className="text-sm font-medium">Statutory Compliance</span>
                </div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Active</span>
              </div>
            </div>
          </div>
        </div>

        {/* Professional CTA */}
        <div className="text-center pt-16 border-t border-slate-100">
          <h3 className="text-xl font-bold text-gray-900 mb-2" style={poppinsStyle}>Stop tracking manually. Start automating.</h3>
          <p className="text-sm text-gray-500 mb-8">Ready to sync your team's schedule with precision?</p>
          
          <div className="flex justify-center">
            <Link 
              to="/login"
              className="group flex items-center gap-3 bg-gray-900 hover:bg-black text-white px-6 py-2.5 rounded-full transition-all duration-300 shadow-md"
            >
              <div className="text-left">
                <p className="text-[9px] uppercase tracking-widest opacity-70 leading-none mb-1" style={poppinsStyle}>Access Suite</p>
                <p className="text-sm font-semibold leading-none" style={poppinsStyle}>Get Started Now</p>
              </div>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}